import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

/**
 * Secure hook for client portal to fetch candidates
 * Only selects non-PII fields to prevent data exposure via network inspection
 */
export const useClientCandidates = (filters = {}) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCandidates();
  }, [JSON.stringify(filters)]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);

      // SECURITY: Only select non-PII fields for client portal
      // Do NOT include: first_name, last_name, email, phone, or any other PII
      let query = supabase
        .from('candidates')
        .select(`
          id,
          status,
          clearance_level,
          location_city,
          location_state,
          notice_period,
          citizenship_status,
          position_id,
          position:positions_master(id, name, category),
          candidate_skills(
            id,
            level,
            skill:skills_master(id, name, category)
          )
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.clearance_level) {
        query = query.eq('clearance_level', filters.clearance_level);
      }
      if (filters.location_state) {
        query = query.eq('location_state', filters.location_state);
      }
      if (filters.notice_period) {
        query = query.eq('notice_period', filters.notice_period);
      }

      const { data, error } = await query;

      if (error) throw error;

      let filteredData = data || [];

      // Client-side filter for skills (since we need to search nested data)
      if (filters.skill) {
        filteredData = filteredData.filter(candidate =>
          candidate.candidate_skills?.some(cs =>
            cs.skill?.name?.toLowerCase().includes(filters.skill.toLowerCase())
          )
        );
      }

      setCandidates(filteredData);
      setError(null);
    } catch (err) {
      console.error('Error fetching candidates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { candidates, loading, error, refetch: fetchCandidates };
};
